import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Users, Target, Briefcase, GraduationCap, Globe, Heart,
  Lightbulb, Brain, Handshake, ArrowRight, Building2, Rocket
} from 'lucide-react';
import { Button } from '../components/ui/button';

const PAYPAL_LINK = "https://www.paypal.com/donate/?hosted_button_id=ZUX3BKRN524VS";

const partners = [
  { name: 'OPAD', role: 'Lead Coordinator', type: 'lead' },
  { name: 'Health Poverty Action', role: 'Strategic Partner', type: 'strategic' },
  { name: 'Save the Children', role: 'Strategic Partner', type: 'strategic' },
  { name: 'Cuso International', role: 'Technical Partner', type: 'technical' },
  { name: 'Uniwater Canada', role: 'Technical Partner', type: 'technical' },
  { name: 'Penal Reform Ethiopia', role: 'Strategic Partner', type: 'strategic' },
];

const focusAreas = [
  { icon: GraduationCap, title: 'Skills Development & TVET', description: 'Technical and Vocational Education and Training for employable skills', color: '#7BC4E2' },
  { icon: Briefcase, title: 'Employment & Entrepreneurship', description: 'Job creation, business support, and startup incubation', color: '#4A7C59' },
  { icon: Lightbulb, title: 'Digital Innovation', description: 'Technology solutions and digital literacy programs', color: '#E5B75F' },
  { icon: Brain, title: 'Mental Health & Well-being', description: 'Psychosocial support and mental health services for youth', color: '#E08B7A' },
  { icon: Handshake, title: 'Public-Private Partnerships', description: 'Collaboration with government, private sector, and academia', color: '#7BC4E2' },
];

const expectedResults = [
  { value: '10,000+', label: 'Youth Trained' },
  { value: '5,000+', label: 'Jobs Created' },
  { value: '500+', label: 'Enterprises Supported' },
  { value: '3', label: 'Countries Expanded' },
];

export default function ConsortiumPage() {
  return (
    <div className="min-h-screen bg-[#FDF8F4]">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#4A7C59] via-[#5A8C69] to-[#4A7C59]">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#E5B75F]/20 rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6">
                <Users className="w-5 h-5 text-white" />
                <span className="text-white text-sm font-semibold">Multi-Partner Initiative</span>
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-['Manrope'] text-white mb-6 leading-tight">
                OPAD Consortium for Youth Skills Development and Employment
              </h1>
              
              <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
                A transformative multi-partner initiative empowering youth with skills, innovation, 
                and sustainable employment opportunities across Africa.
              </p>

              <div className="flex flex-wrap gap-4 justify-center">
                <Link to="/partnership">
                  <Button size="lg" className="bg-white text-[#4A7C59] hover:bg-white/90 rounded-full px-8 py-6 text-lg font-semibold">
                    <Handshake className="w-5 h-5 mr-2" />
                    Join the Consortium
                  </Button>
                </Link>
                <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#4A7C59] rounded-full px-8 py-6 text-lg font-semibold">
                    <Heart className="w-5 h-5 mr-2" />
                    Support Our Work
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <motion.div
              className="bg-[#4A7C59]/5 rounded-2xl p-8 border-l-4 border-[#4A7C59]"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Target className="w-12 h-12 text-[#4A7C59] mb-4" />
              <h2 className="text-2xl font-bold font-['Manrope'] text-slate-900 mb-4">Our Vision</h2>
              <p className="text-slate-600 text-lg">
                A future where young people across Africa have access to skills, opportunities, 
                and sustainable employment.
              </p>
            </motion.div>

            <motion.div
              className="bg-[#E08B7A]/5 rounded-2xl p-8 border-l-4 border-[#E08B7A]"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Rocket className="w-12 h-12 text-[#E08B7A] mb-4" />
              <h2 className="text-2xl font-bold font-['Manrope'] text-slate-900 mb-4">Our Mission</h2>
              <p className="text-slate-600 text-lg">
                To empower youth through skills development, innovation, and partnerships 
                that drive inclusive economic growth.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Strategic Approach */}
      <section className="py-16 bg-[#FDF8F4]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900 mb-4">
              Strategic Approach: Pilot → Scale → Regional Impact
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Starting in Ethiopia with scalable models designed for expansion across Africa.
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-4 max-w-3xl mx-auto">
            {[
              { step: '1', title: 'Launch in Ethiopia', desc: 'Initial pilot programs' },
              { step: '2', title: 'Develop Scalable Models', desc: 'Proven frameworks' },
              { step: '3', title: 'Expand Across Africa', desc: 'Regional replication' },
            ].map((item, index) => (
              <React.Fragment key={item.step}>
                <motion.div
                  className="bg-white rounded-2xl p-6 shadow-lg text-center flex-1"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="w-12 h-12 bg-[#4A7C59] text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-3">
                    {item.step}
                  </div>
                  <h3 className="font-bold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-slate-500 text-sm">{item.desc}</p>
                </motion.div>
                {index < 2 && (
                  <ArrowRight className="w-8 h-8 text-[#4A7C59] hidden md:block flex-shrink-0" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Focus Areas */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900 mb-4">
              Key Focus Areas
            </h2>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {focusAreas.map((area, index) => (
              <motion.div
                key={area.title}
                className="bg-[#FDF8F4] rounded-2xl p-6 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: `${area.color}20` }}
                >
                  <area.icon className="w-7 h-7" style={{ color: area.color }} />
                </div>
                <h3 className="font-bold text-slate-900 text-sm mb-2">{area.title}</h3>
                <p className="text-slate-500 text-xs">{area.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Consortium Partners */}
      <section className="py-16 bg-slate-900">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-white mb-4">
              Consortium Partners
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              A coalition of international organizations, local institutions, and technical experts 
              working together for youth empowerment.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {partners.map((partner, index) => (
              <motion.div
                key={partner.name}
                className={`rounded-2xl p-6 text-center ${
                  partner.type === 'lead' 
                    ? 'bg-[#E5B75F] text-slate-900' 
                    : 'bg-white/10 text-white'
                }`}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Building2 className={`w-10 h-10 mx-auto mb-3 ${
                  partner.type === 'lead' ? 'text-slate-900' : 'text-[#E5B75F]'
                }`} />
                <h3 className="font-bold text-lg mb-1">{partner.name}</h3>
                <p className={`text-sm ${partner.type === 'lead' ? 'text-slate-700' : 'text-slate-400'}`}>
                  {partner.role}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Partner Structure */}
          <div className="mt-12 max-w-2xl mx-auto">
            <div className="bg-white/5 rounded-2xl p-6">
              <h3 className="text-white font-bold text-center mb-6">Consortium Structure</h3>
              <div className="space-y-4 text-center text-sm">
                <div className="bg-[#E5B75F] text-slate-900 rounded-lg px-4 py-2 font-bold">
                  OPAD (Lead Coordinator)
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-[#7BC4E2]/20 text-[#7BC4E2] rounded-lg px-3 py-2">
                    Technical Partners
                  </div>
                  <div className="bg-[#4A7C59]/20 text-[#4A7C59] rounded-lg px-3 py-2">
                    Implementing Partners
                  </div>
                  <div className="bg-[#E08B7A]/20 text-[#E08B7A] rounded-lg px-3 py-2">
                    Strategic Partners
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expected Results */}
      <section className="py-16 bg-[#4A7C59]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-white mb-4">
              Expected Results
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {expectedResults.map((result, index) => (
              <motion.div
                key={result.label}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">{result.value}</div>
                <div className="text-white/80">{result.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Users className="w-16 h-16 text-[#4A7C59] mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">
              Join the Movement
            </h2>
            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
              Partner with the OPAD Consortium to empower youth, create jobs, and drive 
              inclusive economic growth across Africa.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/partnership">
                <Button size="lg" className="bg-[#4A7C59] hover:bg-[#3A6B49] text-white rounded-full px-10 py-6 text-lg font-semibold">
                  <Handshake className="w-5 h-5 mr-2" />
                  Become a Partner
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-2 border-[#4A7C59] text-[#4A7C59] hover:bg-[#4A7C59] hover:text-white rounded-full px-10 py-6 text-lg font-semibold">
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
